import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomerGroupsFormTyping, DiscountItem } from "../../types";
import { useFieldArray, useFormContext } from "react-hook-form";
import BaseMaterialButton from "@reusables/BaseMaterialButton";
import BaseTable from "@reusables/BaseTable";
import { ReactComponent as EditSVG } from "@assets/icons/ic_edit.svg";
import { ReactComponent as TrashSVG } from "@assets/icons/ic_trash.svg";
import MutationProductModal from "./MutationProductModal";
import dayjs from "dayjs";
import { Plus } from "lucide-react";
import { Collapse } from "@mui/material";
import { cn } from "@helpers/utils";

export default function ProductSection() {
    const { t } = useTranslation("", { keyPrefix: "customerGroups.mutation.table" });
    const [selectedItemIndex, setSelectedItemIndex] = useState<number>();
    const [selectedItem, setSelectedItem] = useState<DiscountItem>();
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const { control, clearErrors, formState } = useFormContext<CustomerGroupsFormTyping>();

    const {
        fields: items,
        append,
        update,
        remove,
    } = useFieldArray({
        control,
        name: "items",
        keyName: "loop_id",
    });

    const itemsError = formState.errors.items;
    const itemsErrorMessage =
        typeof itemsError?.message === "string" ? itemsError.message : undefined;

    const closeItemModal = () => {
        setSelectedItem(undefined);
        setSelectedItemIndex(undefined);
        setIsItemModalOpen(false);
    };

    return (
        <>
            <div>
                <div
                    className={cn(
                        "border border-solid rounded-[8px] p-[16px] transition-colors duration-150 ease-out motion-reduce:transition-none",
                        itemsErrorMessage ? "!border-inputs-border-error" : "border-gray-300"
                    )}
                >
                    {items.length ? (
                        <div className="flex flex-col space-y-[16px]">
                            <div className="flex flex-row justify-between items-center">
                                <div className="text-lg font-semibold text-accent">
                                    {t("heading")}
                                </div>
                                <BaseMaterialButton
                                    type="button"
                                    fontSize={14}
                                    fontWeight={400}
                                    onClick={() => setIsItemModalOpen(true)}
                                    className="space-x-2 h-[40px]"
                                >
                                    <Plus size={16} aria-hidden="true" />
                                    <span>{t("buttons.addItem")}</span>
                                </BaseMaterialButton>
                            </div>

                            <BaseTable
                                data={items}
                                immutable
                                rowKeyGetter={(row) => row.loop_id}
                                columns={[
                                    {
                                        header: t("columns.0"),
                                        getter: (row) =>
                                            row.type === "product"
                                                ? row.product.code
                                                : row.category.code,
                                    },
                                    {
                                        header: t("columns.1"),
                                        getter: (row) => (
                                            <div className="flex items-center space-x-2">
                                                <span>
                                                    {row.type === "product"
                                                        ? row.product.name
                                                        : row.category.name}
                                                </span>
                                                {row.type === "category" && (
                                                    <span className="bg-[#E5E5E5] rounded-[16px] px-2 py-1 font-thin text-xs text-[#686868]">
                                                        {t("categoryLabel")}
                                                    </span>
                                                )}
                                            </div>
                                        ),
                                    },
                                    {
                                        header: t("columns.2"),
                                        getter: (row) =>
                                            row.type === "product" ? row.sale_price : "—",
                                    },
                                    {
                                        header: t("columns.3"),
                                        getter: (row) =>
                                            row.valid_from?.format("DD.MM.YYYY") ??
                                            dayjs().format("DD.MM.YYYY"),
                                    },
                                    {
                                        header: t("columns.4"),
                                        getter: (row) => {
                                            if (!row.valid_to) {
                                                return (
                                                    <span className="bg-[#E5E5E5] rounded-[16px] px-2 py-1 font-thin">
                                                        {t("noExpiration")}
                                                    </span>
                                                );
                                            }

                                            const expirationDate = dayjs(row.valid_to);
                                            if (dayjs().isBefore(expirationDate)) {
                                                return expirationDate.format("DD.MM.YYYY");
                                            }

                                            return (
                                                <div className="space-x-2">
                                                    <span>
                                                        {expirationDate.format("DD.MM.YYYY")}
                                                    </span>
                                                    <span className="bg-[#E5E5E5] rounded-[16px] px-2 py-1 font-thin">
                                                        {t("expiredLabel")}
                                                    </span>
                                                </div>
                                            );
                                        },
                                    },
                                    {
                                        header: t("columns.5"),
                                        getter: (row, index) => (
                                            <div className="flex flex-row space-x-[12px]">
                                                <button
                                                    type="button"
                                                    aria-label={t("actions.edit") as string}
                                                    className="inline-flex appearance-none border-0 bg-transparent p-0 shadow-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400"
                                                    onClick={() => {
                                                        setSelectedItem(row);
                                                        setSelectedItemIndex(index);
                                                        setIsItemModalOpen(true);
                                                    }}
                                                >
                                                    <EditSVG
                                                        aria-hidden="true"
                                                        className="text-tables-secondaryIcon hover:text-tables-highlightedIcon cursor-pointer"
                                                    />
                                                </button>
                                                <button
                                                    type="button"
                                                    aria-label={t("actions.delete") as string}
                                                    className="inline-flex appearance-none border-0 bg-transparent p-0 shadow-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400"
                                                    onClick={() => remove(index)}
                                                >
                                                    <TrashSVG
                                                        aria-hidden="true"
                                                        className="text-tables-secondaryIcon hover:text-tables-highlightedIcon cursor-pointer"
                                                    />
                                                </button>
                                            </div>
                                        ),
                                    },
                                ]}
                                size="medium"
                                headerSx={{ background: "#F9F9F9" }}
                            />
                        </div>
                    ) : (
                        <div className="flex justify-center">
                            <div className="flex flex-column items-center justify-center space-y-[24px] h-[128px]">
                                <div className="text-2xl text-center text-gray-300 font-thin">
                                    {t("noItems")}
                                </div>
                                <BaseMaterialButton
                                    type="button"
                                    onClick={() => setIsItemModalOpen(true)}
                                    fontSize={14}
                                    className="w-[240px] h-[48px] space-x-2"
                                >
                                    <Plus size={16} aria-hidden="true" />
                                    <span>{t("buttons.addItem")}</span>
                                </BaseMaterialButton>
                            </div>
                        </div>
                    )}
                </div>

                <Collapse in={!!itemsErrorMessage} timeout={200} unmountOnExit>
                    <div className="mt-[8px] text-sm text-inputs-textError" role="alert">
                        {itemsErrorMessage}
                    </div>
                </Collapse>
            </div>

            <MutationProductModal
                isOpen={isItemModalOpen}
                onClose={closeItemModal}
                onItemAddition={(newItem) => {
                    selectedItemIndex !== undefined
                        ? update(selectedItemIndex, newItem)
                        : append(newItem);
                    clearErrors("items");
                    closeItemModal();
                }}
                item={selectedItem}
            />
        </>
    );
}
